import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FaArrowLeft, FaCalendarAlt, FaChevronRight, FaFileExcel, FaMoneyBillWave, FaRedo, FaUsers } from "react-icons/fa";
import * as XLSX from "xlsx";
import api from "../../api";
import "./SalaryReport.css";
import "./MonthlyDueReportPage.css";

const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const statusPriority = { paid: 0, partial: 1, unpaid: 2, upcoming: 3 };
const getStaffList = (payload) => {
  const data = payload?.data;
  if (Array.isArray(data)) return data;
  return data?.staff || data?.teachers || data?.employees || payload?.staff || payload?.teachers || [];
};
const getMonthList = (payload) => {
  const data = payload?.data;
  if (Array.isArray(data)) return data;
  return data?.months || data?.salary_months || data?.data || payload?.months || payload?.salary_months || [];
};
const getSalaryReportList = (payload) => {
  const data = payload?.data;
  if (Array.isArray(data)) return data;
  return data?.staff || data?.report || data?.rows || payload?.staff || payload?.report || payload?.rows || null;
};
const normalizeSalaryReport = (entries) => (Array.isArray(entries) ? entries.map((entry) => ({
  person: entry?.person || entry?.employee || entry?.staff || entry?.teacher || entry,
  months: Array.isArray(entry?.months) ? entry.months : getMonthList(entry),
})) : []);
const getAcademicYearList = (payload) => {
  const data = payload?.data;
  if (Array.isArray(data)) return data;
  return data?.academic_years || data?.years || data?.sessions || data?.data || payload?.academic_years || payload?.years || payload?.sessions || [];
};
const getAcademicYearId = (academicYear) => academicYear?.id ?? academicYear?.academic_year_id ?? academicYear?.academicYearId;
const getAcademicYearCalendarYear = (academicYear, fallbackYear) => {
  const value = academicYear?.start_date || academicYear?.startDate || academicYear?.start_year || academicYear?.session_name || academicYear?.name || academicYear?.year || "";
  const match = String(value).match(/(?:19|20)\d{2}/);
  return match ? Number(match[0]) : fallbackYear;
};
const toValidDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};
const getAcademicYearYears = (academicYear) => {
  const value = academicYear?.session_name || academicYear?.name || academicYear?.year || "";
  const matches = String(value).match(/(?:19|20)\d{2}/g) || [];
  const startYear = Number(academicYear?.start_year || academicYear?.startYear || matches[0]) || null;
  const endYear = Number(academicYear?.end_year || academicYear?.endYear || matches[1]) || (startYear ? startYear + 1 : null);
  return { startYear, endYear };
};
const getStoredAcademicYearId = () => {
  const idKeys = [
    "academic_year_id", "academicYearId", "selected_academic_year_id",
    "selectedAcademicYearId", "current_academic_year_id", "currentAcademicYearId",
    "selected_year_id", "selectedYearId"
  ];
  for (const key of idKeys) {
    const value = localStorage.getItem(key);
    if (value !== null && String(value).trim() !== "") return String(value).trim();
  }
  const objectKeys = ["academic_year", "academicYear", "selected_academic_year", "selectedAcademicYear", "current_academic_year", "currentAcademicYear"];
  for (const key of objectKeys) {
    const value = localStorage.getItem(key);
    if (!value) continue;
    try {
      const parsed = JSON.parse(value);
      const id = parsed?.id ?? parsed?.academic_year_id ?? parsed?.academicYearId;
      if (id !== undefined && id !== null && String(id).trim() !== "") return String(id).trim();
    } catch {
      // Ignore non-JSON values.
    }
  }
  return "";
};
const money = (value) => `₹${Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const paymentStatus = (record) => {
  const paid = Number(record?.total_paid ?? record?.paid ?? record?.salary_paid ?? 0);
  const due = Number(record?.closing_due ?? record?.due ?? 0);
  const status = String(record?.status || record?.payment_status || "").toLowerCase();
  if (status === "paid" || (paid > 0 && due <= 0)) return "paid";
  return status === "partial" || (paid > 0 && due > 0) ? "partial" : "unpaid";
};
const getMonthNo = (record) => {
  const number = Number(record?.month_number ?? record?.month);
  if (number >= 1 && number <= 12) return number;
  const dateValue = record?.salary_month || record?.month_name || record?.month;
  const timestamp = dateValue && Date.parse(dateValue);
  if (!Number.isNaN(timestamp)) return new Date(timestamp).getMonth() + 1;
  return months.findIndex((name) => name.toLowerCase() === String(dateValue || "").slice(0, 3).toLowerCase()) + 1;
};
const getStaffIdPrefix = (person) => {
  const roleText = [person?.designation, person?.employee_group, person?.employee_type]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  if (/\bcook\b/.test(roleText)) return "K";
  if (/\bpeon\b|\bpoen\b/.test(roleText)) return "P";
  if (/teach/.test(String(person?.employee_type || "").toLowerCase())) return "T";
  return "N";
};
const formatStaffId = (person) => {
  const rawId = person?.user_id ?? person?.staff_id ?? person?.user_account_id ?? person?.account_id ?? person?.id ?? person?.employee_id;
  const digits = String(rawId ?? "").replace(/\D/g, "");
  return digits ? `${getStaffIdPrefix(person)}${digits.padStart(4, "0")}` : "-";
};

export default function SalaryReportPage() {
  const [data, setData] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [academicYearId, setAcademicYearId] = useState(() => getStoredAcademicYearId());
  const [academicYearReady, setAcademicYearReady] = useState(false);
  const [academicYears, setAcademicYears] = useState([]);
  const reportRequestRef = useRef(0);
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;
  const schoolId = localStorage.getItem("school_id") || localStorage.getItem("selected_school_id") || "";
  const todayStart = new Date(currentYear, currentMonth - 1, currentDate.getDate());
  const selectedAcademicYear = academicYears.find((item) => String(getAcademicYearId(item)) === String(academicYearId));
  const salaryYear = getAcademicYearCalendarYear(selectedAcademicYear, currentYear);
  const academicYearStatus = String(selectedAcademicYear?.status || "").toLowerCase();
  const academicYearStart = toValidDate(selectedAcademicYear?.start_date || selectedAcademicYear?.startDate || selectedAcademicYear?.start);
  const academicYearEnd = toValidDate(selectedAcademicYear?.end_date || selectedAcademicYear?.endDate || selectedAcademicYear?.end);
  const { startYear: academicYearStartYear, endYear: academicYearEndYear } = getAcademicYearYears(selectedAcademicYear);
  const academicYearIsPast = academicYearStatus === "expired" || Boolean(academicYearEnd && academicYearEnd < todayStart) || Boolean(academicYearEndYear && academicYearEndYear < currentYear);
  const academicYearIsFuture = academicYearStatus === "upcoming" || Boolean(academicYearStart && academicYearStart > todayStart) || Boolean(academicYearStartYear && academicYearStartYear > currentYear);
  const isUpcomingMonth = useCallback((monthNumber) => !academicYearIsPast && (academicYearIsFuture || (salaryYear === currentYear && monthNumber > currentMonth) || salaryYear > currentYear), [academicYearIsPast, academicYearIsFuture, salaryYear, currentYear, currentMonth]);

  useEffect(() => {
    let active = true;
    const loadActiveAcademicYear = async () => {
      try {
        const response = await api.get("/school/academic_year/list", {
          params: { school_id: schoolId || undefined, _ts: Date.now() }
        });
        const years = getAcademicYearList(response.data);
        if (active) setAcademicYears(years);
        const storedId = getStoredAcademicYearId();
        const selected = years.find((item) => String(getAcademicYearId(item)) === String(storedId)) || years.find((item) => String(item.status || "").toLowerCase() === "active") || years[0];
        const selectedId = getAcademicYearId(selected);
        if (active && selectedId !== undefined && selectedId !== null && String(selectedId) !== String(storedId)) {
          setAcademicYearId(String(selectedId));
          localStorage.setItem("academic_year_id", String(selectedId));
        }
      } catch {
        // The salary endpoint can still load without an academic-year filter.
      } finally {
        if (active) setAcademicYearReady(true);
      }
    };
    loadActiveAcademicYear();
    return () => { active = false; };
  }, [schoolId]);

  const loadReport = async () => {
    const requestId = ++reportRequestRef.current;
    try {
      setLoading(true); setError("");
      const reportParams = { year: salaryYear, academic_year_id: academicYearId || undefined, school_id: schoolId || undefined, _ts: Date.now() };
      // Newer backends return the complete report in one grouped response.
      // Keep the old per-staff flow below as a compatibility fallback.
      try {
        const reportResponse = await api.get("/salary/report", { params: reportParams });
        const report = getSalaryReportList(reportResponse.data);
        if (Array.isArray(report)) {
          if (requestId === reportRequestRef.current) setData(normalizeSalaryReport(report));
          return;
        }
      } catch {
        // Older deployments may not have the batch endpoint yet.
      }
      const response = await api.get("/teachers/list", { params: { school_id: schoolId || undefined, _ts: Date.now() } });
      const staff = getStaffList(response.data);
      const result = new Array(staff.length);
      let nextStaffIndex = 0;
      let completedStaff = 0;
      const loadStaffChunk = async () => {
        while (nextStaffIndex < staff.length) {
          const index = nextStaffIndex++;
          const person = staff[index];
          try {
            const salaryResponse = await api.get("/salary/monthly-status", {
              params: { employee_id: person.id, year: salaryYear, academic_year_id: academicYearId || undefined, school_id: schoolId || undefined, _ts: Date.now() }
            });
            result[index] = { person, months: getMonthList(salaryResponse.data) };
          } catch {
            result[index] = { person, months: [] };
          }
          completedStaff += 1;
          // Render completed chunks immediately so the report becomes useful
          // while a slower legacy endpoint is still finishing the remaining staff.
          if (requestId === reportRequestRef.current && (completedStaff % 8 === 0 || completedStaff === staff.length)) {
            setData(result.filter(Boolean));
          }
        }
      };
      await Promise.all(Array.from({ length: Math.min(8, staff.length) }, loadStaffChunk));
      if (requestId === reportRequestRef.current) setData(result);
    } catch (err) {
      if (requestId === reportRequestRef.current) setError(err.response?.data?.message || err.message || "Unable to load salary report.");
    } finally {
      if (requestId === reportRequestRef.current) setLoading(false);
    }
  };
  useEffect(() => {
    if (academicYearReady) loadReport();
  }, [currentYear, salaryYear, academicYearId, academicYearReady]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setData([]);
    setSelectedMonth(null);
  }, [academicYearId]);

  useEffect(() => {
    const idKeys = [
      "academic_year_id", "academicYearId", "selected_academic_year_id",
      "selectedAcademicYearId", "current_academic_year_id", "currentAcademicYearId",
      "selected_year_id", "selectedYearId"
    ];
    const updateAcademicYear = (event) => {
      const detail = event?.detail;
      const eventId = detail?.academicYearId ?? detail?.academic_year_id ?? detail?.academicYear?.id ?? detail?.academic_year?.id ?? detail?.id ?? (typeof detail === "string" || typeof detail === "number" ? detail : undefined);
      const nextId = String(eventId ?? getStoredAcademicYearId() ?? "").trim();
      setAcademicYearId((previous) => previous === nextId ? previous : nextId);
    };
    const handleStorage = (event) => {
      if (idKeys.includes(event.key) || event.key === null) updateAcademicYear(event);
    };
    const handleFocus = () => updateAcademicYear();
    ["academicYearChanged", "academic_year_changed", "academic-year-changed"].forEach((name) => window.addEventListener(name, updateAcademicYear));
    window.addEventListener("storage", handleStorage);
    window.addEventListener("focus", handleFocus);
    return () => {
      ["academicYearChanged", "academic_year_changed", "academic-year-changed"].forEach((name) => window.removeEventListener(name, updateAcademicYear));
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("focus", handleFocus);
    };
  }, []);

  const rows = useMemo(() => data.flatMap(({ person, months: salaryMonths }) => months.map((_, index) => {
    // The API may omit a month when there is no payment. Keep that staff member
    // in the report and treat the missing record as unpaid.
    const record = salaryMonths.find((item) => getMonthNo(item) === index + 1) || {};
    const upcoming = isUpcomingMonth(index + 1);
    const salary = upcoming ? 0 : Number(record.net_salary ?? record.total_salary ?? record.salary ?? person.salary ?? person.monthly_salary ?? 0);
    const paid = upcoming ? 0 : Number(record.total_paid ?? record.paid ?? record.salary_paid ?? 0);
    const due = upcoming ? 0 : Number(record.closing_due ?? record.due ?? Math.max(salary - paid, 0));
    const staffId = formatStaffId(person);
    return { id: `${staffId}-${index + 1}`, staffId, name: person.name || person.full_name || "-", type: person.employee_type || "-", month: index + 1, salary, paid, due, status: upcoming ? "upcoming" : paymentStatus({ ...record, paid, due }), transactions: paid > 0 ? Number(record.transaction_count || record.transactions || 1) : 0 };
  })), [data, isUpcomingMonth]);
  const selectedRows = useMemo(() => rows.filter((row) => row.month === selectedMonth), [rows, selectedMonth]);
  const visibleRows = useMemo(() => selectedRows
    .filter((row) => statusFilter === "all" || row.status === statusFilter)
    .sort((a, b) => (statusPriority[a.status] ?? 99) - (statusPriority[b.status] ?? 99)), [selectedRows, statusFilter]);
  const totals = useMemo(() => visibleRows.reduce((sum, row) => ({ salary: sum.salary + row.salary, paid: sum.paid + row.paid, due: sum.due + row.due }), { salary: 0, paid: 0, due: 0 }), [visibleRows]);
  const monthCards = useMemo(() => months.map((name, index) => {
    const entries = rows.filter((row) => row.month === index + 1);
    return { name, number: index + 1, entries, paid: entries.reduce((sum, row) => sum + row.paid, 0), due: entries.reduce((sum, row) => sum + row.due, 0) };
  }), [rows]);
  const reportTotals = useMemo(() => rows.reduce((sum, row) => ({ salary: sum.salary + row.salary, paid: sum.paid + row.paid, due: sum.due + row.due, unpaid: sum.unpaid + (row.status === "unpaid" ? 1 : 0) }), { salary: 0, paid: 0, due: 0, unpaid: 0 }), [rows]);
  const exportExcel = () => {
    if (!visibleRows.length) return;
    const sheet = XLSX.utils.json_to_sheet(visibleRows.map((row) => ({ "Staff ID": row.staffId, Staff: row.name, Type: row.type, Month: months[row.month - 1], Salary: row.salary, Paid: row.paid, Due: row.due, Status: row.status, Transactions: row.transactions })));
    const book = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(book, sheet, `${months[selectedMonth - 1]} Salary`);
    XLSX.writeFile(book, `salary-report-${months[selectedMonth - 1].toLowerCase()}.xlsx`);
  };

  return <div className="md-page salary-report-page">
    <div className="md-page-header"><div><div className="md-title-icon"><FaCalendarAlt /></div><div><h1>Salary Report</h1><p>Month-wise staff salary payment and due report</p></div></div><div className="salary-due-actions"><button className="md-excel-btn" onClick={exportExcel} disabled={!visibleRows.length}><FaFileExcel /> Excel</button><button className="md-refresh-btn" onClick={loadReport}><FaRedo /> Refresh</button></div></div>
    {error && <div className="salary-report-error">{error}</div>}
    {!selectedMonth && <><div className="md-summary-grid"><div className="md-summary-card"><div className="md-summary-card-icon"><FaMoneyBillWave /></div><div><span>Total Salary Due</span><h2 className="salary-red-total"><AnimatedMoney value={reportTotals.due} /></h2></div></div><div className="md-summary-card"><div className="md-summary-card-icon blue"><FaMoneyBillWave /></div><div><span>Total Salary</span><h2><AnimatedMoney value={reportTotals.salary} /></h2></div></div><div className="md-summary-card"><div className="md-summary-card-icon purple"><FaUsers /></div><div><span>Unpaid Staff Months</span><h2>{reportTotals.unpaid}</h2></div></div></div><section className="md-report-card"><div className="md-card-header salary-card-header"><div><h2>Month Wise Salary</h2><p>{loading ? "Loading monthly salary report..." : "Each month shows paid, partial and unpaid staff salary."}</p></div><div><span>Total Due</span><strong><AnimatedMoney value={reportTotals.due} /></strong></div></div><div className="md-table-wrap"><table className="md-table salary-month-table"><thead><tr><th>Month</th><th>Total Staff</th><th>Paid</th><th>Partial</th><th>Unpaid</th><th>Paid Amount</th><th>Due Amount</th><th>Action</th></tr></thead><tbody>{monthCards.map((card) => { const paid = card.entries.filter((row) => row.status === "paid").length; const partial = card.entries.filter((row) => row.status === "partial").length; const unpaid = card.entries.filter((row) => row.status === "unpaid").length; const upcoming = isUpcomingMonth(card.number); return <tr key={card.name}><td><div className="md-month"><div className="md-month-box">{card.name}</div><div><strong>{months[card.number - 1]}</strong><span>{upcoming ? "Upcoming" : "Salary"}</span></div></div></td><td>{card.entries.length}</td><td className="md-zero">{paid}</td><td className="partial-text">{partial}</td><td className="md-due-amount">{unpaid}</td><td className="md-zero">{money(card.paid)}</td><td className="md-due-amount">{money(card.due)}</td><td><button className="md-view-btn" onClick={() => { setSelectedMonth(card.number); setStatusFilter("all"); }}>View <FaChevronRight /></button></td></tr>; })}</tbody></table></div></section></>}
    {selectedMonth && <section className="salary-detail-report"><div className="breakdown-top"><button className="back-to-months" onClick={() => setSelectedMonth(null)}><FaArrowLeft /> All Months</button></div><div className="section-heading detail-heading"><div><h2>{months[selectedMonth - 1]} Salary Breakdown</h2><p>See who received salary, who is unpaid, and the total amount due.</p></div><label>Payment Status<select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}><option value="all">All</option><option value="paid">Paid</option><option value="partial">Partial</option><option value="unpaid">Unpaid</option><option value="upcoming">Upcoming</option></select></label></div><div className="salary-summary-grid"><Summary icon={<FaMoneyBillWave />} label="Total Salary" value={<AnimatedMoney value={totals.salary} />} color="#2563eb" /><Summary icon={<FaMoneyBillWave />} label="Total Paid" value={<AnimatedMoney value={totals.paid} />} color="#15803d" /><Summary icon={<FaMoneyBillWave />} label="Payment Due" value={<AnimatedMoney value={totals.due} />} color="#dc2626" /><Summary icon={<FaUsers />} label="Staff Shown" value={visibleRows.length} color="#7c3aed" /></div><div className="salary-table-wrap"><table><thead><tr><th>Staff ID</th><th>Staff</th><th>Type</th><th>Salary</th><th>Paid</th><th>Due</th><th>Status</th><th>Transactions</th></tr></thead><tbody>{visibleRows.map((row) => <tr key={row.id} className={`salary-detail-row ${row.status}`}><td><b>{row.staffId}</b></td><td><b>{row.name}</b></td><td>{row.type}</td><td>{row.status === "upcoming" ? "—" : money(row.salary)}</td><td className="paid-text">{money(row.paid)}</td><td className="unpaid-text">{money(row.due)}</td><td><span className={`report-status ${row.status}`}>{row.status}</span></td><td>{row.transactions}</td></tr>)}</tbody></table>{!loading && !visibleRows.length && <div className="salary-report-empty">No {statusFilter === "all" ? "salary" : statusFilter} records found for {months[selectedMonth - 1]}.</div>}</div></section>}
  </div>;
}
function AnimatedMoney({ value }) {
  const target = Number(value || 0);
  const [displayValue, setDisplayValue] = useState(target);
  const displayRef = useRef(target);

  useEffect(() => {
    const start = displayRef.current;
    const difference = target - start;
    if (difference === 0) return undefined;
    const startedAt = performance.now();
    let frameId;
    const animate = (timestamp) => {
      const progress = Math.min((timestamp - startedAt) / 550, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const nextValue = start + (difference * eased);
      displayRef.current = nextValue;
      setDisplayValue(nextValue);
      if (progress < 1) frameId = requestAnimationFrame(animate);
    };
    frameId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frameId);
  }, [target]);

  return <>{money(displayValue)}</>;
}

function Summary({ icon, label, value, color }) {
  const background = color === "#15803d" ? "#dcfce7" : color === "#dc2626" ? "#fee2e2" : color === "#7c3aed" ? "#f3e8ff" : "#e0f2fe";
  return <div className="salary-summary-card"><span style={{ color, background }}>{icon}</span><div><small>{label}</small><strong>{value}</strong></div></div>;
}
